import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { LockIcon } from 'lucide-react';
import { ScaledViewport } from './ScaledViewport';
import { EsteveWordmark } from './EsteveWordmark';
import { CaprelsaSnak } from './snak/CaprelsaSnak';
import { CaprelsaWordmark } from './snak/CaprelsaWordmark';
type Mode = 'phone' | 'desktop';
type LivePrototypeProps = {
  /** Increment to restart the Snak and draw attention to it. */
  playKey: number;
};

/** The playable Caprelsa Snak, shown in a phone or desktop frame. */
export function LivePrototype({
  playKey
}: LivePrototypeProps) {
  const [mode, setMode] = useState<Mode>('phone');
  const [pulse, setPulse] = useState(false);
  const deviceRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (playKey === 0) return;
    setPulse(true);
    const focusTimer = setTimeout(() => {
      deviceRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus({
        preventScroll: true
      });
    }, 250);
    const pulseTimer = setTimeout(() => setPulse(false), 1400);
    return () => {
      clearTimeout(focusTimer);
      clearTimeout(pulseTimer);
    };
  }, [playKey]);
  return <div className="flex h-full w-full flex-col items-center" data-id="element-161" data-mp="u6t28251pcre">
      <div className="flex w-full flex-shrink-0 items-center justify-between gap-4" data-id="element-162" data-mp="gf4pbwmz2k9d">
        <p className="flex items-center gap-2 text-[clamp(0.6rem,0.8vw,0.74rem)] font-bold uppercase tracking-[0.22em] text-[var(--fg)]" data-id="element-163" data-mp="kg6nea55kq9r">
          <span className="relative flex h-2 w-2" data-id="element-164" data-mp="182n1rujxipru">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--brand)] opacity-50" data-id="element-165" data-mp="1d2rgpg56vcfy" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--brand)]" data-id="element-166" data-mp="z3jd8b1zti5y" />
          </span>
          Live prototype
        </p>
        <div role="radiogroup" aria-label="Preview device" className="flex rounded-full border border-[var(--fg)]/10 bg-white p-0.5 hide-on-print" data-id="element-167" data-mp="3jua7t5unqemp">
          {(['phone', 'desktop'] as const).map(m => <button key={m} type="button" role="radio" aria-checked={mode === m} onClick={() => setMode(m)} className={`relative rounded-full px-3 py-1 text-[clamp(0.58rem,0.75vw,0.7rem)] font-bold uppercase tracking-[0.16em] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] ${mode === m ? 'text-white' : 'text-[var(--muted)] hover:text-[var(--fg)]'}`} data-id="element-168" data-mp="1ucvdih2oggs1">
              {mode === m && <motion.span layoutId="device-toggle" className="absolute inset-0 rounded-full bg-[var(--fg)]" transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-169" data-mp="16m98kynyzcm9" />}
              <span className="relative" data-id="element-170" data-mp="xogqxnurzsvp">{m}</span>
            </button>)}
        </div>
      </div>

      <div ref={deviceRef} data-snak-device className="relative mt-[clamp(0.6rem,1.2vw,1rem)] min-h-0 w-full flex-1" data-id="element-171" data-mp="2w1pcnzn1e85a">
        {mode === 'phone' ? <ScaledViewport baseWidth={390} baseHeight={760} bezel={10} radius={0.13} className="h-full w-full" deviceClassName={`bg-ink shadow-[0_40px_80px_-30px_rgba(14,27,51,0.5)] transition-shadow duration-500 ${pulse ? 'ring-4 ring-[var(--brand)]/40' : ''}`} data-id="element-172" data-mp="dtnt9a0v8wpg">
            <div className="flex h-full flex-col bg-white" data-id="element-173" data-mp="rjr6xcloji3a">
              <CaprelsaSnak key={playKey} data-id="element-174" data-mp="3r0ftyzjb3qf1" />
            </div>
          </ScaledViewport> : <ScaledViewport baseWidth={1240} baseHeight={820} bezel={8} radius={0.018} className="h-full w-full" deviceClassName={`bg-ink shadow-[0_40px_80px_-30px_rgba(14,27,51,0.5)] ${pulse ? 'ring-4 ring-[var(--brand)]/40' : ''}`} data-id="element-175" data-mp="3vxz7tyc3e16g">
            <div className="flex h-full flex-col bg-cap-lilac" data-id="element-176" data-mp="1f9bte5h1kxhb">
              <div className="flex h-10 flex-shrink-0 items-center gap-3 border-b border-black/5 bg-white px-4" data-id="element-177" data-mp="1k8opzhxptix3">
                <span className="flex gap-1.5" aria-hidden="true" data-id="element-178" data-mp="1ir9bzpuxu4wl">
                  <span className="h-3 w-3 rounded-full bg-ink/15" data-id="element-179" data-mp="1dsy1re0qdsdu" />
                  <span className="h-3 w-3 rounded-full bg-ink/15" data-id="element-180" data-mp="1dusl4nwctcft" />
                  <span className="h-3 w-3 rounded-full bg-ink/15" data-id="element-181" data-mp="1rts87r47xj9v" />
                </span>
                <span className="mx-auto flex items-center gap-1.5 rounded-md bg-cap-lilac px-4 py-1 font-mono text-[11px] text-cap-grey" data-id="element-182" data-mp="1nqvvui59ujed">
                  <LockIcon size={10} aria-hidden="true" data-id="element-183" data-mp="21p2a8eum9qnn" />
                  hcp.esteve.com/caprelsa/snak
                </span>
              </div>
              <div className="flex min-h-0 flex-1" data-id="element-184" data-mp="2bh827okvc82u">
                <aside className="flex w-[360px] flex-col justify-between bg-white px-10 py-10" data-id="element-185" data-mp="2bisxxhix5x2a">
                  <div data-id="element-186" data-mp="26jq46t8e3d9m">
                    <CaprelsaWordmark size="lg" data-id="element-187" data-mp="2khefb9cnn61h" />
                    <div className="mt-8 h-2 w-full bg-cap-gold" aria-hidden="true" data-id="element-188" data-mp="2ki6awk6953rr" />
                    <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.16em] text-cap-gold" data-id="element-189" data-mp="2fjct4fjxmqls">For healthcare professionals</p>
                    <p className="mt-3 text-[30px] font-extrabold leading-[1.05] tracking-[-0.03em] text-cap-purple" data-id="element-190" data-mp="2z20n8mq3vi11">When slowing progression matters.</p>
                    <p className="mt-4 text-[15px] leading-snug text-cap-grey" data-id="element-191" data-mp="2q46rpe060sex">A 40-second case in medullary thyroid cancer. One patient. Three considerations. The evidence.</p>
                  </div>
                  <div data-id="element-192" data-mp="2gdc2q1vjbeps">
                    <EsteveWordmark data-id="element-193" data-mp="2gc138e94gt4h" />
                    <p className="mt-3 text-[10px] leading-snug text-cap-grey" data-id="element-194" data-mp="2l4c0tage42tv">Illustrative, unapproved concept for discussion.</p>
                  </div>
                </aside>
                <div className="flex flex-1 items-center justify-center py-3" data-id="element-195" data-mp="26dcw4aiodyfk">
                  <div className="flex h-full w-[400px] flex-col overflow-hidden rounded-xl bg-white shadow-[0_20px_40px_-20px_rgba(43,28,98,0.35)]" data-id="element-196" data-mp="2bf293vpgkm6a">
                    <CaprelsaSnak key={playKey} data-id="element-197" data-mp="22dkxoqmzx8nx" />
                  </div>
                </div>
                <div className="w-[200px]" aria-hidden="true" data-id="element-198" data-mp="7xm99e69qspt" />
              </div>
            </div>
          </ScaledViewport>}
      </div>
    </div>;
}